import React from "react";

import { Button, message, Typography, Upload } from "antd";
import fileDownload from "js-file-download";
import PropTypes from "prop-types";

import { UploadOutlined } from "@ant-design/icons";

import { useAppTheme } from "../../context/ThemeProvider";
import {
  extractHostFromUrl,
  validateEmail,
  validateHost,
} from "../../Utils/utilityFunctions";

const UploadUsersCSV = ({ onUpload, onError }) => {
  const { COLORS } = useAppTheme();

  // CSV parsing function
  const parseCsvData = (csvText) => {
    const lines = csvText.split("\n").filter((line) => line.trim());
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());

    const requiredHeaders = ["name", "email", "host"];
    const missingHeaders = requiredHeaders.filter((h) => !headers.includes(h));

    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(", ")}`);
    }

    const data = [];
    let successCount = 0;
    let failedCount = 0;

    for (let i = 1; i < lines.length; i += 1) {
      const values = lines[i].split(",").map((v) => v.trim());
      const row = {};

      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });

      // Validate required fields
      if (row.name && row.email && row.host) {
        // Basic email and host validation
        const isEmailValid = validateEmail(row.email);
        const extractedHost = extractHostFromUrl(row.host);
        const isHostValid = validateHost(extractedHost);

        if (isEmailValid && isHostValid) {
          data.push({
            name: row.name,
            email: row.email,
            host: extractedHost,
            status: "valid",
          });

          successCount += 1;
        } else {
          data.push({
            name: row.name,
            email: isEmailValid
              ? row.email
              : `${row.email} (Wrong email format)`,
            host: isHostValid
              ? extractedHost
              : `${extractedHost} (Wrong host format)`,
            status: "invalid",
            error:
              (!isEmailValid && "Wrong email format") ||
              (!isHostValid && "Wrong host format") ||
              "",
          });

          failedCount += 1;
        }
      } else {
        data.push({
          name: row.name || "Missing_Name",
          email: row.email || "Missing_Email",
          host: row.host || "Missing_Host",
          status: "invalid",
          error: "Missing required fields",
        });

        failedCount += 1;
      }
    }

    return { data, successCount, failedCount };
  };

  // Handle CSV file upload
  const handleCsvUpload = (uploadedFile) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const result = parseCsvData(csvText);
        onUpload(
          uploadedFile,
          result.data,
          result.successCount,
          result.failedCount
        );
      } catch (error) {
        onError(error.message || "Error parsing CSV file");
      }
    };
    reader.readAsText(uploadedFile);
  };

  // Download sample CSV
  const downloadSampleCSV = () => {
    const sampleData = [
      ["John Doe", "john.doe@example.com", "learn.johndoe.com"],
      ["Jane Smith", "jane.smith@example.com", "business.janesmith.com"],
      ["Mike Johnson", "mike.johnson@example.com", "academy.mikejohnson.com"],
    ];

    const csvColumnNames = ["name", "email", "host"];
    const csvContent = sampleData.map((row) => row.join(",")).join("\n");
    const csv = `${csvColumnNames}\n${csvContent}`;

    fileDownload(csv, `CSV Template for Adding Page for Users.csv`);
    message.success("Sample CSV downloaded successfully!");
  };

  return (
    <div className="upload-users-csv">
      <div className="upload-header" style={{ marginBottom: 0 }}>
        <Typography.Title level={4} style={{ marginBottom: 0 }}>
          Upload CSV
        </Typography.Title>
      </div>

      <Typography.Text type="secondary" className="upload-description">
        Please upload a CSV file with columns:{" "}
        <strong>name, email, host.</strong>{" "}
        <Button
          type="link"
          onClick={downloadSampleCSV}
          style={{ padding: 0, color: COLORS.INFO }}
        >
          Download sample CSV
        </Button>
      </Typography.Text>

      <div className="upload-dragger-container">
        <Upload.Dragger
          name="csvFile"
          accept=".csv"
          beforeUpload={(uploadFile) => {
            handleCsvUpload(uploadFile);
            return false; // Prevent default upload
          }}
          showUploadList={false}
          className="csv-upload-dragger"
        >
          <p className="ant-upload-drag-icon">
            <UploadOutlined style={{ fontSize: 48 }} />
          </p>

          <p className="ant-upload-text">
            Click or drag CSV file to this area to upload
          </p>

          <p className="ant-upload-hint">
            CSV files are supported only. Required columns:{" "}
            <strong style={{ fontWeight: 600 }}>name, email, host.</strong>
          </p>
        </Upload.Dragger>
      </div>
    </div>
  );
};

UploadUsersCSV.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default UploadUsersCSV;
